import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import KpiCard from "../../components/KpiCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Download, Edit3, History, MonitorUp, RefreshCw, Trash2, UploadCloud, XCircle } from "lucide-react";
import { toast } from "sonner";

const initialForm = {
  appType: "player",
  versionName: "",
  versionCode: "",
  releaseNotes: "",
  forceUpdate: false,
  active: true,
  allowDowngrade: false,
  file: null,
};

function bytes(size) {
  const n = Number(size || 0);
  if (!n) return "0 B";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

export default function AdminAppUpdates() {
  const [updates, setUpdates] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ releaseNotes: "", forceUpdate: false, active: true, allowDowngrade: false });

  const load = useCallback(async () => {
    try {
      const [listRes, dashRes, deviceRes] = await Promise.all([
        api.get("/admin/app-updates", { params: filter === "all" ? {} : { appType: filter } }),
        api.get("/admin/app-updates/dashboard"),
        api.get("/admin/app-updates/device-report"),
      ]);
      setUpdates(listRes.data || []);
      setDashboard(dashRes.data || {});
      setDevices(deviceRes.data || []);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const latestPlayer = dashboard?.latestPlayer;
  const latestController = dashboard?.latestController;
  const filteredDevices = useMemo(() => devices.filter((d) => {
    if (filter === "player") return true;
    if (filter === "controller") return true;
    return true;
  }), [devices, filter]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) {
      toast.error("Select an APK file first.");
      return;
    }
    setBusy(true);
    setUploadProgress(0);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === "file") fd.append("file", value);
        else fd.append(key, value);
      });
      await api.post("/admin/app-updates", fd, {
        timeout: 10 * 60 * 1000,
        onUploadProgress: (event) => {
          if (!event.total) return;
          setUploadProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        },
      });
      setUploadProgress(100);
      toast.success("APK version uploaded");
      setForm(initialForm);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail || err));
    } finally {
      setBusy(false);
      window.setTimeout(() => setUploadProgress(0), 1200);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setEditForm({
      releaseNotes: row.releaseNotes || "",
      forceUpdate: !!row.forceUpdate,
      active: !!row.active,
      allowDowngrade: !!row.allowDowngrade,
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/app-updates/${editing.id}`, editForm);
      toast.success("APK version updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.appType} ${row.versionName}?`)) return;
    try {
      await api.delete(`/admin/app-updates/${row.id}`);
      toast.success("APK deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Download URL copied");
    } catch {
      toast.error("Could not copy URL");
    }
  };

  const remind = async (deviceId) => {
    try {
      await api.post(`/admin/app-updates/devices/${deviceId}/remind`);
      toast.success("Device marked to check again");
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail || e));
    }
  };

  return (
    <div data-testid="admin-app-updates-page">
      <PageHeader
        overline="Admin / App Updates"
        title="APK update management."
        subtitle="Upload Player and Controller app versions, control rollout, and track fleet update status."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6 mb-8">
        <KpiCard label="Player Version" value={latestPlayer?.versionName || "-"} hint={latestPlayer ? `Code ${latestPlayer.versionCode}` : "No active APK"} Icon={MonitorUp} />
        <KpiCard label="Controller Version" value={latestController?.versionName || "-"} hint={latestController ? `Code ${latestController.versionCode}` : "No active APK"} Icon={UploadCloud} accent="#0EA5E9" />
        <KpiCard label="Need Update" value={dashboard?.devicesNeedingUpdate ?? 0} hint="Player devices behind latest" Icon={RefreshCw} accent="#F59E0B" />
        <KpiCard label="Updated" value={dashboard?.devicesUpdated ?? 0} hint="Player devices on latest" Icon={History} accent="#10B981" />
        <KpiCard label="Failed" value={dashboard?.failedUpdates ?? 0} hint="Failed update logs" Icon={XCircle} accent="#EF4444" />
        <KpiCard label="Downloads" value={dashboard?.downloadCount ?? 0} hint="Total APK downloads" Icon={Download} accent="#6366F1" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_1fr]">
        <form onSubmit={submit} className="border border-[#E5E7EB] bg-white rounded-sm p-5 space-y-4">
          <div>
            <div className="text-sm font-bold text-[#111827]">Upload APK</div>
            <div className="text-xs text-[#6B7280]">Use signed release APK files only.</div>
          </div>
          <div>
            <Label>App type</Label>
            <Select value={form.appType} onValueChange={(v) => setForm((f) => ({ ...f, appType: v }))}>
              <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="player">Player</SelectItem>
                <SelectItem value="usb_player">USB Offline Player</SelectItem>
                <SelectItem value="controller">Controller</SelectItem>
                <SelectItem value="society">Society App</SelectItem>
                <SelectItem value="studio">Studio App</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Version name</Label>
              <Input required value={form.versionName} onChange={(e) => setForm((f) => ({ ...f, versionName: e.target.value }))} placeholder="1.0.5" />
            </div>
            <div>
              <Label>Version code</Label>
              <Input required type="number" min="1" value={form.versionCode} onChange={(e) => setForm((f) => ({ ...f, versionCode: e.target.value }))} placeholder="105" />
            </div>
          </div>
          <div>
            <Label>APK file</Label>
            <Input required type="file" accept=".apk,application/vnd.android.package-archive" onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))} />
          </div>
          <div>
            <Label>Release notes</Label>
            <Textarea value={form.releaseNotes} onChange={(e) => setForm((f) => ({ ...f, releaseNotes: e.target.value }))} rows={4} />
          </div>
          {[
            ["forceUpdate", "Force update"],
            ["active", "Active"],
            ["allowDowngrade", "Allow downgrade"],
          ].map(([key, label]) => (
            <div key={key} className="flex items-center justify-between border border-[#E5E7EB] rounded-sm px-3 py-2">
              <Label className="m-0">{label}</Label>
              <Switch checked={!!form[key]} onCheckedChange={(v) => setForm((f) => ({ ...f, [key]: v }))} />
            </div>
          ))}
          <Button disabled={busy} className="w-full rounded-sm">
            <UploadCloud className="mr-2 h-4 w-4" /> {busy ? `Uploading${uploadProgress ? ` ${uploadProgress}%` : "..."}` : "Upload APK"}
          </Button>
          {busy && (
            <div className="h-2 overflow-hidden rounded-sm bg-[#E5E7EB]">
              <div className="h-full bg-[#111827] transition-all" style={{ width: `${uploadProgress || 8}%` }} />
            </div>
          )}
        </form>

        <div className="space-y-6 min-w-0">
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All apps</SelectItem>
                <SelectItem value="player">Player</SelectItem>
                <SelectItem value="usb_player">USB Offline Player</SelectItem>
                <SelectItem value="controller">Controller</SelectItem>
                <SelectItem value="society">Society App</SelectItem>
                <SelectItem value="studio">Studio App</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-hidden rounded-sm border border-[#E5E7EB] bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB]">
                  <TableHead>App</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>APK</TableHead>
                  <TableHead>Force</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead>Installed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {updates.length === 0 && <TableRow><TableCell colSpan={8} className="py-10 text-center text-sm text-[#6B7280]">No APK versions uploaded.</TableCell></TableRow>}
                {updates.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="capitalize">{row.appType}</TableCell>
                    <TableCell>
                      <div className="font-semibold">{row.versionName}</div>
                      <div className="font-mono text-xs text-[#6B7280]">Code {row.versionCode}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{bytes(row.apkSize)}</div>
                      <button type="button" onClick={() => copyUrl(row.downloadUrl)} className="text-xs text-blue-700 hover:underline">Copy signed URL</button>
                    </TableCell>
                    <TableCell>{yesNo(row.forceUpdate)}</TableCell>
                    <TableCell>{yesNo(row.active)}</TableCell>
                    <TableCell>{row.downloadCount || 0}</TableCell>
                    <TableCell>{row.installedCount || 0}</TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(row)}><Edit3 className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(row)}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="overflow-hidden rounded-sm border border-[#E5E7EB] bg-white">
            <div className="border-b border-[#E5E7EB] px-4 py-3 text-sm font-bold">Installed Version Report</div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9FAFB]">
                  <TableHead>Device</TableHead>
                  <TableHead>Client / Dealer</TableHead>
                  <TableHead>Player</TableHead>
                  <TableHead>Controller</TableHead>
                  <TableHead>USB Cache</TableHead>
                  <TableHead>Last Check</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDevices.map((device) => (
                  <TableRow key={device.deviceId}>
                    <TableCell>
                      <div className="font-semibold">{device.deviceName}</div>
                      <div className="font-mono text-xs text-[#6B7280]">{device.deviceCode}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{device.clientName || "-"}</div>
                      <div className="text-xs text-[#6B7280]">{device.dealerName || "-"}</div>
                    </TableCell>
                    <TableCell>{device.currentPlayerVersion || 0}</TableCell>
                    <TableCell>{device.currentControllerVersion || 0}</TableCell>
                    <TableCell>
                      <div className="text-sm">{device.usbCachedFileCount || 0} files</div>
                      <div className="text-xs text-[#6B7280]">{device.usbPlayerVersion ? `v${device.usbPlayerVersion}` : "USB -"}</div>
                    </TableCell>
                    <TableCell className="text-xs">{device.lastUpdateCheck || "-"}</TableCell>
                    <TableCell>
                      <div className="text-sm">{device.lastUpdateStatus || "-"}</div>
                      {device.lastError && <div className="text-xs text-red-600">{device.lastError}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => remind(device.deviceId)}>Check again</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Edit APK Version</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Release notes</Label>
              <Textarea rows={5} value={editForm.releaseNotes} onChange={(e) => setEditForm((f) => ({ ...f, releaseNotes: e.target.value }))} />
            </div>
            {[
              ["forceUpdate", "Force update"],
              ["active", "Active"],
              ["allowDowngrade", "Allow downgrade"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between border border-[#E5E7EB] rounded-sm px-3 py-2">
                <Label className="m-0">{label}</Label>
                <Switch checked={!!editForm[key]} onCheckedChange={(v) => setEditForm((f) => ({ ...f, [key]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="rounded-sm" onClick={saveEdit}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
